/**
 * Exact decimal arithmetic for Platform percentage splits (R4.5).
 *
 * The split column is `numeric(5,2)` (design.md, "Platform association with
 * primary and split") and R4.5 requires the entered percentages to total
 * *exactly* 100.00. Binary floating point cannot answer that question: the
 * legitimate split `33.33 + 33.33 + 33.34` sums to `100.00000000000001` as
 * doubles, and an epsilon comparison would accept `33.34 + 33.34 + 33.33`
 * (100.01) on some inputs. So percentages travel as canonical decimal text -
 * which is also how PostgreSQL hands back `numeric` - and all arithmetic happens
 * on integer hundredths, where every value in range is exactly representable.
 *
 * The parser is deliberately strict rather than forgiving: `0.001` is a
 * precision violation the user must see, not a value to round.
 */
/** Decimal places `numeric(5,2)` permits, and therefore R4.5 permits. */
export const PERCENT_DECIMAL_PLACES = 2;
/** Scale factor between {@link PercentText} and {@link PercentHundredths}. */
export const HUNDREDTHS_PER_PERCENT = 100;
/** Lowest percentage R4.5 accepts: 0.01. */
export const MIN_PERCENT_HUNDREDTHS = 1;
/** Highest percentage R4.5 accepts: 100.00. */
export const MAX_PERCENT_HUNDREDTHS = 100 * HUNDREDTHS_PER_PERCENT;
/** The total R4.5 requires a complete split to reach: exactly 100.00. */
export const REQUIRED_SPLIT_TOTAL_HUNDREDTHS = MAX_PERCENT_HUNDREDTHS;
/**
 * Unsigned decimal text with no exponent, no leading `+`, no leading zeros
 * beyond a single `0`, and no trailing dot. Anything else is `malformed`;
 * a well-shaped value with too much precision is reported as such instead.
 */
const DECIMAL_TEXT = /^(0|[1-9][0-9]*)(?:\.([0-9]+))?$/;
/**
 * Parses canonical decimal text into exact hundredths.
 *
 * Total: every string maps either to a value in 0.01 to 100.00 or to one of the
 * three named failures.
 */
export function parsePercent(text) {
    const match = DECIMAL_TEXT.exec(text);
    if (match === null) {
        return { ok: false, failure: 'malformed' };
    }
    const integerText = match[1] ?? '';
    const fractionText = match[2] ?? '';
    if (fractionText.length > PERCENT_DECIMAL_PLACES) {
        return { ok: false, failure: 'too_many_decimal_places' };
    }
    const hundredths = Number(integerText) * HUNDREDTHS_PER_PERCENT +
        Number(fractionText.padEnd(PERCENT_DECIMAL_PLACES, '0'));
    if (hundredths < MIN_PERCENT_HUNDREDTHS || hundredths > MAX_PERCENT_HUNDREDTHS) {
        return { ok: false, failure: 'out_of_range' };
    }
    return { ok: true, hundredths };
}
/**
 * Renders exact hundredths back to decimal text with two decimal places, so a
 * reported total reads as the user entered it (`'99.99'`, not `'99.99000001'`).
 *
 * Accepts values outside the 0.01 to 100.00 range on purpose: the entered total
 * reported by a split violation is frequently outside it.
 */
export function formatPercent(hundredths) {
    const rounded = Math.round(hundredths);
    const magnitude = Math.abs(rounded);
    const whole = Math.floor(magnitude / HUNDREDTHS_PER_PERCENT);
    const fraction = magnitude % HUNDREDTHS_PER_PERCENT;
    const sign = rounded < 0 ? '-' : '';
    return `${sign}${String(whole)}.${String(fraction).padStart(PERCENT_DECIMAL_PLACES, '0')}`;
}
/**
 * Exact sum of percentages. Integer addition, so no rounding error accumulates
 * across the up to ten members of a Platform set.
 */
export function sumPercent(values) {
    return values.reduce((total, value) => total + value, 0);
}
/**
 * Converts a numeric percentage to canonical decimal text without rounding.
 *
 * The API boundary receives JSON numbers; this is where they become storage
 * shaped. It deliberately does not round: `33.333` becomes `'33.333'` and is
 * then rejected as a precision violation rather than silently becoming `33.33`.
 */
export function percentTextFromNumber(value) {
    return String(value);
}
//# sourceMappingURL=percent.js.map